import { useState } from "react";
import { PageHeader, Card, CardHeader, AtomBadge } from "../components/primitives";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AtomExplainer() {
  const [mode, setMode] = useState("decision");
  const [generating, setGenerating] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [ctx, setCtx] = useState({ subject_name: "Babatunde Adeyemi", subject_id: "2014823091", profile: "Tier 3 retail, Trader, Lagos", rules: "RULE-STR-001 (sub-CTR structuring)", observed: "14 cash deposits ₦4.82M–₦4.96M over 11 days, multi-branch", baseline: "₦340K monthly cash deposits", amount: 67340000, typology: "T1003", cbn: "5.5" });

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/atom/narrative", ctx);
      setNarrative(r.data.narrative);
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Atom Explainer"
        subtitle="Glass-box reasoning for any decision or model"
        sections={["5.9"]}
        atom={<AtomBadge model="Atom Nucleus" version="v3.5" description="Reasoning trace generation" />}
      />
      <div className="px-6 py-2 bg-bg-surface border-b border-line flex items-center gap-2 text-[12px]">
        <span className="text-ink-subtle">Mode:</span>
        {[["decision","Decision explainability"],["governance","Model governance explainability"]].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} className={`px-2 py-1 rounded-sm border ${mode===v?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>{l}</button>
        ))}
      </div>
      <div className="p-6 grid grid-cols-3 gap-3">
        {mode === "decision" && (
          <>
            <Card className="col-span-1">
              <CardHeader title="Input context" subtitle="Ad-hoc narrative generation" />
              <div className="p-3 space-y-2 text-[12px]">
                {Object.entries(ctx).map(([k, v]) => (
                  <div key={k}>
                    <label className="block text-[10px] uppercase tracking-wider text-ink-subtle">{k}</label>
                    <input value={v} onChange={(e) => setCtx({...ctx, [k]: e.target.value})} className="w-full border border-line rounded-sm px-2 py-1 text-[12px] bg-bg-surface" />
                  </div>
                ))}
                <button onClick={generate} disabled={generating} data-testid="generate-narrative" className="w-full mt-2 px-3 py-2 text-[12px] bg-navy text-white rounded-sm disabled:opacity-50">
                  {generating ? "Generating via Atom Nucleus…" : "Generate narrative"}
                </button>
              </div>
            </Card>
            <Card className="col-span-2">
              <CardHeader title="Reasoning trace" subtitle="Plain-English audit-grade explanation" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
              <div className="p-5 border-l-2 border-amber/40 bg-amber/4 m-4 text-[13px] leading-relaxed whitespace-pre-wrap min-h-[200px]" data-testid="atom-narrative">
                {narrative || "Click 'Generate narrative' to produce an audit-grade explanation. Pre-seeded typology contexts return a pre-generated narrative; ad-hoc contexts route to Atom Nucleus v3.5 via the live model endpoint."}
              </div>
              <div className="px-4 pb-4 grid grid-cols-3 gap-2 text-[11px]">
                <div className="border border-line rounded-sm p-2"><div className="text-ink-subtle">CBN section</div><div className="font-medium">{ctx.cbn}</div></div>
                <div className="border border-line rounded-sm p-2"><div className="text-ink-subtle">AMLTRIX</div><div className="font-medium">{ctx.typology}</div></div>
                <div className="border border-line rounded-sm p-2"><div className="text-ink-subtle">Explainability</div><div className="font-medium tabular">0.92</div></div>
              </div>
            </Card>
            <Card className="col-span-3">
              <CardHeader title="Examiner walkthrough export" subtitle="A one-page PDF for handover to a CBN examiner" />
              <div className="p-3 text-[12px] text-ink-muted">
                Use the export button on the alert or case detail page to produce the examiner-ready walkthrough document. The PDF includes the subject identification, the rule and model contributions, the AMLTRIX and CBN citations, and the Atom narrative — formatted for direct handover.
              </div>
            </Card>
          </>
        )}
        {mode === "governance" && (
          <Card className="col-span-3">
            <CardHeader title="Model governance explainability" right={<AtomBadge model="Atom Photon" version="v1.0" />} />
            <div className="p-5 text-[13px] leading-relaxed">
              Atom Photon v1.0 is the cross-cutting governance model. It assesses the explainability of every other Atom model on the platform, monitors drift across all features, computes fairness diagnostics across customer demographic groups, and schedules retraining and validation cycles. Photon's outputs are consumed by the Model Risk Management module and feed the Examination Readiness composite score.
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="border border-line rounded-sm p-3">
                  <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Drift detection</div>
                  <div className="text-[12px]">Atom Proton v2.4 · counterparty-risk feature exceeds 0.05 PSI · retraining flagged</div>
                </div>
                <div className="border border-line rounded-sm p-3">
                  <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Fairness</div>
                  <div className="text-[12px]">No statistically significant bias detected across customer demographic groups in last 90 days</div>
                </div>
                <div className="border border-line rounded-sm p-3">
                  <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Validation schedule</div>
                  <div className="text-[12px]">Atom Electron annual validation · 14 Apr 2027 · independent</div>
                </div>
                <div className="border border-line rounded-sm p-3">
                  <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Explainability scores</div>
                  <div className="text-[12px]">All models above 0.85 threshold; Atom Nucleus highest at 0.92</div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
